import type { ConstraintTreeTarget } from '@/portfolio/proposal-builder/data/transformers/constraints-to-risk-factors'
import type { ModelConstraint } from '@/types/api/constraints'

import { recomputeFactorAggregates } from '@/portfolio/proposal-builder/data/helpers/recompute-factor-aggregates'

// Clears a constraint id from every tree slot so an edited constraint can be
// re-placed without leaving a stale copy behind. Factors the removal touched
// get their aggregates recomputed so the removed constraint's scope/priority
// can't be stamped back onto survivors at serialization.
export function removeConstraintFromTree(
  target: ConstraintTreeTarget,
  constraintId: string,
): void {
  for (const riskFactor of Object.values(target.riskFactors)) {
    let removed = false
    const drop = (constraint: ModelConstraint | undefined): boolean => {
      if (constraint?.id !== constraintId) return false
      removed = true
      return true
    }
    const dropFrom = <T extends ModelConstraint>(constraints: T[]): T[] => {
      const kept = constraints.filter((c) => c.id !== constraintId)
      if (kept.length !== constraints.length) removed = true
      return kept
    }

    const filterListConstraints = riskFactor.filterListConstraints
    if (filterListConstraints) {
      if (drop(filterListConstraints.inclusionConstraint)) {
        filterListConstraints.inclusionConstraint = undefined
      }
      if (drop(filterListConstraints.exclusionConstraint)) {
        filterListConstraints.exclusionConstraint = undefined
      }
      filterListConstraints.allocationConstraints = dropFrom(
        filterListConstraints.allocationConstraints,
      )
    }

    for (const fieldConstraints of Object.values(riskFactor.fields)) {
      if (!fieldConstraints) continue
      if (drop(fieldConstraints.universeConstraint)) {
        fieldConstraints.universeConstraint = undefined
      }
      if (drop(fieldConstraints.diversificationConstraint)) {
        fieldConstraints.diversificationConstraint = undefined
      }
      if (drop(fieldConstraints.piecewiseDiversificationConstraint)) {
        fieldConstraints.piecewiseDiversificationConstraint = undefined
      }
      if (drop(fieldConstraints.averageValueConstraint)) {
        fieldConstraints.averageValueConstraint = undefined
      }
      fieldConstraints.allocationConstraints = dropFrom(
        fieldConstraints.allocationConstraints,
      )
    }

    if (removed) {
      recomputeFactorAggregates(riskFactor)
    }
  }

  target.otherConstraints = target.otherConstraints.filter(
    (constraint) => constraint.id !== constraintId,
  )

  if (target.ladderPolicy?.id === constraintId) {
    target.ladderPolicy = undefined
  }
}
